import angular from "angular";
import { defaultFormatter, IValueFormatter } from "./formatters";

export const vsName = "csvWriter";

export type CsvConfig = {
  includeHeaders?: boolean;
  columns: CsvColumnConfig[];
};

export class CsvWriter {
  public includeHeaders: boolean = true;

  private readonly $window: angular.IWindowService;

  public readonly formatters: {
    [formatterName: string]: IValueFormatter;
  };

  constructor(
    $window: angular.IWindowService,
    config: {
      formatters?: { [formatterName: string]: IValueFormatter };
      includeHeaders?: boolean;
    }
  ) {
    this.formatters = config.formatters ?? {};
    this.includeHeaders = config.includeHeaders ?? true;
    this.$window = $window;
  }

  getCsv(config: CsvConfig, rows: any[]): string {
    const result: string[] = [];
    const { columns, includeHeaders } = config;

    if (includeHeaders ?? this.includeHeaders) {
      result.push(this.getHeader(columns));
    }

    result.push(...rows.map((c) => this.formatLine(c, columns)));

    return result.join("\n");
  }

  saveFile(params: { content: string | Blob; filename: string }) {
    const blob = new Blob([params.content], { type: "text/csv" });
    const blobUrl = this.$window.URL.createObjectURL(blob);
    const downloadLink = angular.element("<a></a>");
    downloadLink.attr("href", blobUrl);
    downloadLink.attr("download", params.filename);
    downloadLink[0].click();
    downloadLink.remove();
  }

  private getHeader(columns: CsvColumnConfig[]): string {
    const fieldHeaders = columns.map((c) =>
      this.escapeCsvValue(c.header ?? c.field)
    );
    return fieldHeaders.join(",");
  }

  private formatLine(row: any, columns: CsvColumnConfig[]): string {
    const cellValues = columns
      .map((c) =>
        this.getFormatter(c)({
          cellValue: row[c.field],
          row: row,
        })
      )
      .map((formattedValue) => this.escapeCsvValue(formattedValue));

    return cellValues.join(",");
  }

  private getFormatter(column: CsvColumnConfig): IValueFormatter {
    if (column.formatter) {
      return column.formatter;
    }

    if (column.format && this.formatters[column.format]) {
      return this.formatters[column.format];
    }

    return defaultFormatter;
  }

  private static readonly specialCharsRegex = /[,"\n]/g;

  private escapeCsvValue(value: string | undefined): string {
    if (value === undefined) {
      return "";
    }

    const hasSpecialChar = CsvWriter.specialCharsRegex.test(value);
    if (!hasSpecialChar) {
      return value;
    }

    const escapedValue = `"${value
      .replace('"', '""')
      .replace(/\r\n/g, "  ")
      .replace(/\n/g, "  ")}"`;

    return escapedValue;
  }
}

export interface CsvColumnConfig {
  field: string;
  header?: string;
  format?: string;
  formatter?: IValueFormatter;
}
